import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDetailDriveFolder } from "@modules/folderTreeView";
import { getDriveDetailAPI, getDrivesListAPI } from "@modules/apis/virtureFolder";
import moment from "moment";

const useVirtualFolderPath = ({ domain, subDomain, driveInfo }) => {
    const dispatch = useDispatch();

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const getVirtualDrive = useCallback(async () => {
        const headerParams = {
            projectId: project.id,
        };
        const params = {
            resourceTypeId: driveInfo?.resourceTypeId,
        };

        const drives = await getDrivesListAPI(stage.endpoint, {
            headerParams,
            params,
        });
        return drives.data?.results?.find((el) => el.resourceType?.type === domain);
    }, [domain, driveInfo?.resourceTypeId, project.id, stage.endpoint]);

    const getVirtualFolder = useCallback(
        async ({ driveId, folderId }) => {
            let _driveId = driveId;
            let _folderId = folderId;

            if (!_driveId) {
                const drive = await getVirtualDrive();
                _driveId = drive.id;
                if (!_folderId) {
                    _folderId = drive.rootFolder?.id;
                }
            }
            const folderPath = await dispatch(
                getDetailDriveFolder({
                    driveId: _driveId,
                    folderId: _folderId,
                }),
            );
            return { driveId: _driveId, folderPath };
        },
        [dispatch, getVirtualDrive],
    );

    const getVirtualFolderFullPath = useCallback(
        async ({ driveId, folderId }) => {
            if (!folderId) return undefined;
            try {
                const { folderPath } = await getVirtualFolder({ driveId, folderId });

                const fullPath = folderPath.parents
                    .reverse()
                    .reduce((acc, cur) => `${cur.name}/${acc}`, `${folderPath.name}`);
                return fullPath;
            } catch (e) {
                console.log(e);
                return undefined;
            }
        },
        [getVirtualFolder],
    );

    const getVirtualFolderFullPathList = async ({ folderId, includeFolder }) => {
        try {
            if (!folderId) {
                const drive = await getVirtualDrive();
                return [drive];
            } else {
                const { folderPath } = await getVirtualFolder({ folderId });
                if (includeFolder) {
                    return [...folderPath.parents, folderPath];
                } else {
                    return folderPath.parents;
                }
            }
        } catch (e) {
            console.log(e);
            return undefined;
        }
    };

    const getLocalStorageRecentLocation = async () => {
        const key = subDomain ? `${domain.toLowerCase()}${subDomain}` : domain.toLowerCase();
        const recentLocation = JSON.parse(localStorage.getItem(`${key}RecentLocation`)) || [];
        let newLocations = [];
        try {
            const filteredRecentLocation = recentLocation?.filter((el) => el.projectId && el.stageId);
            localStorage.setItem(`${key}RecentLocation`, JSON.stringify(filteredRecentLocation));

            const targetRecentLocation = recentLocation.filter(
                (el) => el.stageId === stage.id && el.projectId === project.id,
            );
            if (targetRecentLocation.length > 0) {
                const drive = await getVirtualDrive();
                const detailPromises = targetRecentLocation
                    .sort((a, b) => b.createdAt - a.createdAt)
                    ?.map(async ({ value }) => {
                        const folderId = value;
                        const label = await getVirtualFolderFullPath({ driveId: drive?.id, folderId });
                        if (label) newLocations.push({ value, label });
                    });
                await Promise.all(detailPromises);
            }
            return newLocations;
        } catch (e) {
            console.log(e);
        }
    };

    const setLocalStorageRecentLocation = ({ folderId, folderPathValue }) => {
        const key = subDomain ? `${domain.toLowerCase()}${subDomain}` : domain.toLowerCase();
        const recentLocation = JSON.parse(localStorage.getItem(`${key}RecentLocation`)) || [];

        // 이미 존재하는 항목 제거
        const filteredLocation = recentLocation.filter((item) => item.value !== folderId);

        // 새로운 항목 추가
        const current = moment().format("YYYY-MM-DD HH:mm:ss");
        const newObj = {
            stageId: stage.id,
            projectId: project.id,
            value: folderId,
            createdAt: current,
            folderPath: folderPathValue,
        };

        // 새 항목을 앞에 추가하고 최대 5개만 유지
        const newRecentLocation = [newObj, ...filteredLocation].slice(0, 5);
        localStorage.setItem(`${key}RecentLocation`, JSON.stringify(newRecentLocation));
    };

    const getDriveName = useCallback(
        async ({ driveId }) => {
            const driveInfo = await getDriveDetailAPI(stage.endpoint, {
                driveId,
                headerParams: { projectId: project.id },
            });
            return driveInfo.data.name;
        },
        [stage, project],
    );

    return {
        getVirtualDrive,
        getVirtualFolder,
        getVirtualFolderFullPath,
        getVirtualFolderFullPathList,
        getLocalStorageRecentLocation,
        setLocalStorageRecentLocation,
        getDriveName,
    };
};

export default useVirtualFolderPath;
